import { type ComponentPropsWithoutRef } from "react";
import { type UseSelectReturnValue } from "downshift";
import { FloatingPortal } from "@floating-ui/react";
import { CoreSelect, type PropsWithOptionalTestID } from "@boi/components-core";
import { useDesignTokens } from "@boi/react-design-tokens";

import * as S from "./SelectBase.styled";
import { useFloatingMenu } from "./SelectBase.utils";
import { useOverlay } from "../OverlayContext";
import { ScrollShadowContainer } from "../ScrollShadowContainer";
import { AlertAssociationProps } from "../Alert/AlertAssociationProps";

export type SelectBaseProps<T extends CoreSelect.SelectBaseItem> =
  PropsWithOptionalTestID &
    Pick<
      UseSelectReturnValue<T>,
      "getToggleButtonProps" | "getMenuProps" | "isOpen"
    > & {
      items: T[];
      toggleButtonProps?: Pick<
        ComponentPropsWithoutRef<"div">,
        "aria-describedby"
      >;
      renderButtonContent: () => JSX.Element;
      renderListItem: (item: T, index: number) => JSX.Element;
      hasError?: boolean;
      isDisabled?: boolean;
      maxListHeight?: number;
      alertAssociationProps?: AlertAssociationProps;
    };

function SelectBase<T extends CoreSelect.SelectBaseItem>(
  props: SelectBaseProps<T>
) {
  const {
    getToggleButtonProps,
    getMenuProps,
    isOpen,
    items,
    toggleButtonProps,
    renderButtonContent,
    renderListItem,
    hasError,
    isDisabled,
    maxListHeight: suggestedMaxListHeight,
    alertAssociationProps,
    testID = "select",
  } = props;

  const { tokens } = useDesignTokens();
  const { select } = tokens;

  const { refs, floatingStyles, availableListHeight } = useFloatingMenu({
    offsetValue: parseInt(select.gap, 10),
  });

  const menuOptions = { ref: refs.setFloating };
  const menuOtherProps = { suppressRefError: true };

  const { ref: menuContainerRef, ...menuProps } = getMenuProps(
    menuOptions,
    menuOtherProps
  );

  const { portalElement } = useOverlay();

  let maxListHeight = suggestedMaxListHeight;

  if (
    availableListHeight &&
    suggestedMaxListHeight &&
    availableListHeight < suggestedMaxListHeight
  ) {
    maxListHeight = availableListHeight;
  }

  return (
    <>
      <S.ButtonWrapper>
        <S.Button
          {...toggleButtonProps}
          {...getToggleButtonProps({
            ref: refs.setReference,
            disabled: isDisabled,
          })}
          $isOpen={isOpen}
          $hasError={hasError}
          $isDisabled={isDisabled}
          data-testid={testID}
          {...alertAssociationProps}
        >
          <S.ButtonContent>
            {renderButtonContent()}

            <S.ButtonIconWrapper>
              {isOpen ? (
                <S.ButtonOpenIcon aria-hidden="true" />
              ) : (
                <S.ButtonClosedIcon aria-hidden="true" />
              )}
            </S.ButtonIconWrapper>
          </S.ButtonContent>
        </S.Button>
      </S.ButtonWrapper>

      <FloatingPortal root={portalElement}>
        <S.ListContainer
          ref={menuContainerRef}
          style={floatingStyles}
          $isOpen={isOpen}
        >
          <div style={{ maxHeight: maxListHeight }}>
            <ScrollShadowContainer>
              <S.List {...menuProps}>
                {items.map((item, index) => renderListItem(item, index))}
              </S.List>
            </ScrollShadowContainer>
          </div>
        </S.ListContainer>
      </FloatingPortal>
    </>
  );
}

export default SelectBase;
